import React, { useState, useEffect } from "react";
import { Typography, CircularProgress } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { getParams, searchParams } from "../../../../utlis/helpers";
import { ACTIVITY_MONITORING } from "../../../../routes/routerPath";
import { getCarePlan } from "../../../../services/v5Api";
import { userInfoFetch } from "../../../../utlis/commonFunction";
import {
  formatLastUpdated,
  shouldShowMobility,
  shouldShowActivity,
  shouldShowVitals,
  filterVitalsForDisplay,
  transformCarePlanResponse,
} from "../carePlan/carePlanUtils";
import StatusMetricBlockV2 from "../../../common/StatusMetricBlockV2";
import ActivityAbnormalitiesV2 from "../../../common/ActivityAbnormalitiesV2";
import NoDataFound from "../../../common/NoDataFound";
import { rewriteReportDomain } from "../../../../configs/environment";

// Import PNG icons
import caregivingIcon from "../../../../assets/icons/caregiving.png";
import therapistIcon from "../../../../assets/icons/therapist.png";
import clinicianIcon from "../../../../assets/icons/clinician.png";
import mobilityIcon from "../../../../assets/icons/mobility.png";
import activityIcon from "../../../../assets/icons/activity.png";
import vitalsIcon from "../../../../assets/icons/vitals.png";

import "./careplanv2.scss";

const ROLES = [
  { id: "staff", label: "Care Staff", icon: caregivingIcon },
  { id: "therapist", label: "Therapist", icon: therapistIcon },
  { id: "clinician", label: "Clinician", icon: clinicianIcon },
];

const ROLE_KEY_MAP = {
  staff: ["caregiving_staff", "caregiving", "caregiv"],
  therapist: ["therapists", "therapist", "pt"],
  clinician: ["clinicians", "clinician", "doctor"],
};

/**
 * Find the recommendation text for a given role tab ID.
 */
const getRecommendationForRole = (recommendations, roleId) => {
  if (!recommendations || recommendations.length === 0) return null;
  const keys = ROLE_KEY_MAP[roleId] || [];
  for (const rec of recommendations) {
    const roleKey = (rec.role || "").toLowerCase();
    if (keys.some((k) => roleKey.includes(k))) return rec;
  }
  return recommendations[0] || null;
};

/**
 * Collapsible section with icon, title, status pill, and expandable content.
 */
const CollapsibleSection = ({
  icon,
  title,
  meta,
  statusLabel,
  statusType,
  children,
  defaultOpen = false,
}) => {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div className={`cpv2-collapsible ${open ? "cpv2-collapsible--open" : ""}`}>
      <button
        className="cpv2-collapsible__header"
        onClick={() => setOpen((o) => !o)}
        type="button"
      >
        <div className="cpv2-collapsible__icon">
          <img src={icon} alt={title} />
        </div>
        <div className="cpv2-collapsible__title-area">
          <div className="cpv2-collapsible__title-row">
            <span className="cpv2-collapsible__title">{title}</span>
            {statusLabel && (
              <span
                className={`cpv2-status-pill cpv2-status-pill--${statusType || "info"}`}
              >
                {statusLabel}
              </span>
            )}
          </div>
          {meta && <span className="cpv2-collapsible__meta">{meta}</span>}
        </div>
        <svg
          className={`cpv2-chevron ${open ? "cpv2-chevron--open" : ""}`}
          width="14"
          height="14"
          viewBox="0 0 14 14"
          fill="none"
        >
          <path
            d="M3 5L7 9L11 5"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
      {open && <div className="cpv2-collapsible__body">{children}</div>}
    </div>
  );
};

/**
 * CarePlanV2 Component
 *
 * Redesigned care plan with:
 * - Role selector tabs (Care Staff / Therapist / Clinician)
 * - Urgency banner for fall alerts
 * - Per-role "Care Action" block
 * - Collapsible "Inputs & Insights" accordion sections
 */
const CarePlanV2 = ({ onNavigateTab, onOpenMobilityInsights }) => {
  const navigate = useNavigate();
  const [carePlanData, setCarePlanData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [role, setRole] = useState("staff");

  const userInfo = userInfoFetch();
  const seniorUuid = userInfo?.id;

  useEffect(() => {
    const loadCarePlanData = async () => {
      if (!seniorUuid) {
        setIsLoading(false);
        return;
      }
      setIsLoading(true);
      try {
        const response = await getCarePlan(seniorUuid);
        const apiData = response?.data || response;
        const transformed = transformCarePlanResponse(apiData);
        setCarePlanData(transformed);
      } catch (error) {
        console.error("Error loading care plan data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    loadCarePlanData();
  }, [seniorUuid]);

  const handleActivityTrend = () => {
    navigate(ACTIVITY_MONITORING + searchParams);
  };

  const buildRomReportUrl = (rawUrl) => {
    if (!rawUrl) return rawUrl;
    const { accessToken, providerId, adminId, clientId } = getParams();
    const withV2 = String(rawUrl).replace(
      /side-tug-reportv1/g,
      "side-tug-reportv2",
    );
    // Force the configured REPORTS_URL domain regardless of what the backend returned.
    const domainRewritten = rewriteReportDomain(withV2);
    try {
      const url = new URL(domainRewritten);
      const paramsToAdd = {
        isWebsite: "true",
        accessToken,
        providerId,
        adminId,
        clientId,
      };
      Object.entries(paramsToAdd).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          if (!url.searchParams.has(key)) {
            url.searchParams.set(key, value);
          }
        }
      });
      return url.toString();
    } catch {
      return domainRewritten;
    }
  };

  const handleWellnessReport = (url) => {
    if (url) {
      window.location.href = buildRomReportUrl(url);
    }
  };

  // --- Loading ---
  if (isLoading) {
    return (
      <div className="cpv2-container cpv2-container--loading">
        <CircularProgress size={32} />
      </div>
    );
  }

  // --- No data ---
  if (!carePlanData) {
    return (
      <div className="cpv2-container">
        <NoDataFound
          icon="care"
          message="No care plan available"
          description="The care plan will appear here once it has been generated for this person."
        />
      </div>
    );
  }

  const { lastUpdated, recommendations, insights } = carePlanData;
  const { mobility, activity, vitals } = insights || {};
  const showMobility = shouldShowMobility(mobility);
  const showActivity = shouldShowActivity(activity);
  const showVitals = shouldShowVitals(vitals);
  const vitalsToMonitor = filterVitalsForDisplay(vitals?.metrics || []);

  const activeRec = getRecommendationForRole(recommendations, role);

  // Determine urgency: fall detected
  const fallCount = activity?.fallAlerts?.fall?.count ?? 0;
  const hasFallUrgency = fallCount > 0;

  // Mobility risk status
  const mobilityRiskLevel = mobility?.riskTier?.level;
  const mobilityRiskLabel = mobility?.riskTier?.label;
  const mobilityStatusType =
    mobilityRiskLevel === "high"
      ? "error"
      : mobilityRiskLevel === "elevated"
        ? "warning"
        : "success";

  // Vitals status
  const hasElevatedVitals = vitalsToMonitor.some(
    (v) => v.status === "fair" || v.status === "poor",
  );
  const vitalsStatusLabel = hasElevatedVitals ? "Needs attention" : "Normal";
  const vitalsStatusType = hasElevatedVitals ? "warning" : "success";

  // Activity status
  const pfallCount = activity?.fallAlerts?.pfall?.count ?? 0;
  const activityStatusLabel =
    fallCount > 0
      ? "Fall detected"
      : pfallCount > 0
        ? "Possible fall"
        : "Normal";
  const activityStatusType =
    fallCount > 0 ? "error" : pfallCount > 0 ? "warning" : "success";

  return (
    <div className="cpv2-container">
      {/* Last Updated */}
      <div className="cpv2-updated">
        Updated {formatLastUpdated(lastUpdated)}
      </div>

      {/* Role Selector */}
      <div className="cpv2-role-selector">
        {ROLES.map((r) => (
          <button
            key={r.id}
            className={`cpv2-role-btn ${role === r.id ? "cpv2-role-btn--active" : ""}`}
            onClick={() => setRole(r.id)}
            type="button"
          >
            {r.label}
          </button>
        ))}
      </div>

      {/* Urgency Banner */}
      {hasFallUrgency && (
        <div className="cpv2-urgency-banner">
          <span className="cpv2-urgency-banner__text">
            Fall detected — Activity Monitor
          </span>
        </div>
      )}

      {/* Care Action */}
      {activeRec && (
        <div className="cpv2-care-action">
          <div className="cpv2-care-action__label">Care Action</div>
          <Typography className="cpv2-care-action__text">
            {activeRec.text}
          </Typography>
        </div>
      )}

      {/* Divider */}
      <div className="cpv2-divider">
        <span className="cpv2-divider__line" />
        <span className="cpv2-divider__text">Inputs & Insights</span>
        <span className="cpv2-divider__line" />
      </div>

      {/* Risk Insights */}
      {showMobility && (
        <CollapsibleSection
          icon={mobilityIcon}
          title="Risk Insights"
          meta="Mobility risk assessment"
          statusLabel={mobilityRiskLabel}
          statusType={mobilityStatusType}
        >
          <div className="cpv2-section-content">
            <div className="cpv2-metric-row">
              <StatusMetricBlockV2
                label="Mobility Risk Tier"
                value={mobility.riskTier.label}
                status={mobility.riskTier.status || mobility.riskTier.level}
              />
            </div>

            {mobility.reportId && (
              <button
                type="button"
                className="cpv2-link-btn"
                onClick={() => onOpenMobilityInsights?.(mobility.reportId)}
              >
                See Risk Analysis
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <path
                    d="M3 9L9 3M6 3H9V6"
                    stroke="currentColor"
                    strokeWidth="1.4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            )}

            {!mobility.reportId && (
              <div className="cpv2-no-data-prompt">
                <Typography className="cpv2-no-data-text">
                  No risk analysis available yet. Conduct a mobility assessment
                  to generate insights.
                </Typography>
                <button
                  className="btn btn-outline"
                  onClick={() => onNavigateTab?.("risk")}
                >
                  Start Mobility Assessment
                </button>
              </div>
            )}
          </div>
        </CollapsibleSection>
      )}

      {/* Mobility Metrics */}
      {showMobility && mobility.metricsPerformance?.length > 0 && (
        <CollapsibleSection
          icon={mobilityIcon}
          title="Mobility Metrics"
          meta="Performance across domains"
          statusLabel={
            mobility.metricsPerformance.every(
              (m) => m.status === "good" || m.status === "low",
            )
              ? "All Good"
              : "Needs attention"
          }
          statusType={
            mobility.metricsPerformance.every(
              (m) => m.status === "good" || m.status === "low",
            )
              ? "success"
              : "warning"
          }
        >
          <div className="cpv2-section-content">
            <div className="metrics-grid">
              {mobility.metricsPerformance.map((metric) => (
                <StatusMetricBlockV2
                  key={metric.key}
                  label={metric.label}
                  value={metric.statusLabel}
                  status={metric.status}
                />
              ))}
            </div>

            {mobility.wellnessReportUrl && (
              <button
                type="button"
                className="cpv2-link-btn"
                onClick={() => handleWellnessReport(mobility.wellnessReportUrl)}
              >
                See Wellness Report & Exercise
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <path
                    d="M3 9L9 3M6 3H9V6"
                    stroke="currentColor"
                    strokeWidth="1.4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            )}

            {!mobility.wellnessReportUrl && (
              <div className="cpv2-no-data-prompt">
                <Typography className="cpv2-no-data-text">
                  Complete a mobility test to receive a personalized wellness
                  report and exercise plan.
                </Typography>
                <button
                  className="btn btn-outline"
                  onClick={() => onNavigateTab?.("metrics")}
                >
                  Go to Metrics
                </button>
              </div>
            )}
          </div>
        </CollapsibleSection>
      )}

      {/* Activity Monitor */}
      {showActivity && (
        <CollapsibleSection
          icon={activityIcon}
          title="Activity Monitor"
          meta="Fall & activity alerts"
          statusLabel={activityStatusLabel}
          statusType={activityStatusType}
          defaultOpen={hasFallUrgency}
        >
          <div className="cpv2-section-content">
            <ActivityAbnormalitiesV2
              fallAlerts={activity.fallAlerts}
              abnormalities={activity.abnormalities}
            />
            <button
              className="cpv2-link-btn"
              type="button"
              onClick={handleActivityTrend}
            >
              See Activity Trend
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                <path
                  d="M3 9L9 3M6 3H9V6"
                  stroke="currentColor"
                  strokeWidth="1.4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>
        </CollapsibleSection>
      )}

      {/* Vitals */}
      {showVitals && (
        <CollapsibleSection
          icon={vitalsIcon}
          title="Vitals"
          meta="Health metrics overview"
          statusLabel={vitalsStatusLabel}
          statusType={vitalsStatusType}
        >
          <div className="cpv2-section-content">
            <div className="metrics-grid">
              {vitalsToMonitor.map((vital) => (
                <StatusMetricBlockV2
                  key={vital.key}
                  label={vital.label}
                  value={vital.displayValue}
                  status={vital.status}
                />
              ))}
            </div>

            {!vitals?.metrics?.some((v) => v.status !== "no_data") && (
              <div className="cpv2-no-data-prompt">
                <Typography className="cpv2-no-data-text">
                  No vitals data recorded yet. Collect vitals readings to track
                  trends.
                </Typography>
                <button
                  className="btn btn-outline"
                  onClick={() => onNavigateTab?.("monitor")}
                >
                  Go to Monitor
                </button>
              </div>
            )}
          </div>
        </CollapsibleSection>
      )}
    </div>
  );
};

export default CarePlanV2;
